import type { ReactNode } from 'react'
import clsx from 'clsx'
import { DisabledReason } from './DisabledReason'

export interface SelectorFooterAction {
  id: string
  label: string
  icon?: ReactNode
  disabled?: boolean
  /** Why the action is disabled; its tooltip while `disabled`. */
  disabledReason?: string
  /** `danger` draws an action that ends something (Sign out) in red. */
  tone?: 'danger'
  onClick?: () => void
}

interface SelectorFooterProps {
  actions: SelectorFooterAction[]
  className?: string
  /**
   * `sidebar`: in a sidebar menu, spaced like its rows (the rail's inset
   * around and between the buttons, each a rail tile tall).
   */
  spacing?: 'default' | 'sidebar'
}

export default function SelectorFooter({
  actions,
  className,
  spacing = 'default',
}: SelectorFooterProps) {
  if (actions.length === 0) return null

  return (
    <div
      className={clsx(
        // On whatever holds it: a popover, a side panel, or the table drawer.
        'box-border flex shrink-0 items-stretch border-t',
        spacing === 'sidebar'
          ? 'h-[calc(var(--sidebar-tile-size)+var(--sidebar-item-inset)*2+1px)] border-[color:var(--sidebar-submenu-border)] p-[var(--sidebar-item-inset)]'
          : 'h-12 border-default px-2 py-2',
        className
      )}
    >
      <div
        className={clsx(
          'flex min-w-0 flex-1',
          spacing === 'sidebar' ? 'gap-[var(--sidebar-item-inset)]' : 'gap-2'
        )}
      >
        {actions.map((action) => (
          <DisabledReason
            key={action.id}
            reason={action.disabled ? action.disabledReason : null}
            className="min-w-0 flex-1"
            fullWidth
          >
            <button
              type="button"
              disabled={action.disabled}
              onClick={action.onClick}
              className={clsx(
                'inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md border border-default px-3 text-xs font-medium leading-none transition-colors',
                action.disabled
                  ? 'cursor-not-allowed bg-lighter text-faint'
                  : action.tone === 'danger'
                    ? 'surface-default text-danger hover:border-[color:var(--border-danger-subtle)] hover:bg-[color:var(--surface-danger-subtle)]'
                    : 'surface-default text-subtle hover:border-[color:var(--border-strong)] hover:bg-lighter hover:text-default'
              )}
            >
              {action.icon}
              <span className="truncate">{action.label}</span>
            </button>
          </DisabledReason>
        ))}
      </div>
    </div>
  )
}
